import React, { useState } from 'react';
import { AppBar, Drawer, Toolbar, IconButton, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import clsx from 'clsx';
import { Link, useHistory } from 'react-router-dom';
import { UpliftBetterHelpStackedIcon } from '@uplift/icons';
import { routeIsSession, ROUTING_CONFIG } from '@uplift/constants';
import { LightLayoutType } from '@uplift/types';

import { ScrollableView } from '../components/ScrollableView.component';
import { Loader } from '../../Loader';
import { ProfileMenu } from '../components/ProfileMenu';
import { LightLayoutContextProvider } from './light-layout-context';
import { useDrawerStyles } from './right-drawer.styles';
import { useLegacyLayoutClasses } from '../legacyLayoutClasses';


const ConditionalUpliftLink: React.VFC<{ pathname: string }> = ({ pathname }) => {
  if (routeIsSession(pathname)) {
    return (
      <UpliftBetterHelpStackedIcon />
    );
  }

  return (
    <Link to={ROUTING_CONFIG.home} style={{ display: 'flex' }}>
      <UpliftBetterHelpStackedIcon />
    </Link>
  );
};


export type LightLayoutProps = LightLayoutType;


export const LightLayoutProvider: React.FC<LightLayoutProps> = ({
  children,
  onBack,
  leftActions,
  classes: _classes,
  loading,
  showAvatar = true,
  showToolbar = true,
}) => {
  const history = useHistory();
  const { classes } = useLegacyLayoutClasses({ drawerWidth: undefined, classes: _classes });
  const { classes: drawerClasses } = useDrawerStyles();
  const [ showDrawer, setShowDrawer ] = useState(false);
  const [ drawerContent, setDrawerContent ] = useState<React.ReactNode>(null);
  const body = loading ? <Loader color="primary" height="37em" /> : children;
  // Only center the logo when there are actions on the left to balance it (e.g. the rematch flow);
  // otherwise keep the logo left-aligned (e.g. the session-ended screen).
  const centerLogo = Boolean(onBack || leftActions);
  const ctx = React.useMemo(() => ({
    showDrawer,
    setShowDrawer,
    drawerContent,
    setDrawerContent
  }), [ showDrawer, drawerContent ]);

  return (
    <div className={classes.root}>
      {showToolbar && (
        <AppBar position="fixed" elevation={0} className={clsx(classes.appBar, showDrawer && drawerClasses.appBarShift)}>
          <Toolbar className={classes.toolBar}>
            <Box flex={centerLogo ? 1 : '0 0 auto'} display="flex" alignItems="center">
              {onBack && (
                <IconButton onClick={onBack} size="small" className={drawerClasses.backButton}>
                  <ArrowBackIcon />
                </IconButton>
              )}
              {leftActions}
            </Box>

            <Box flex={centerLogo ? 1 : '0 0 auto'} display="flex" justifyContent="center">
              <ConditionalUpliftLink pathname={history?.location?.pathname} />
            </Box>

            <Box flex={1} display="flex" justifyContent="flex-end">
              <ProfileMenu showAvatar={showAvatar} />
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <Box display="flex" flexDirection="column" flex="1" width="100%">
        {showToolbar && <Toolbar />}
        <Box display="flex" flex="1">
          <ScrollableView>
            <LightLayoutContextProvider {...ctx}>
              <main className={drawerClasses.content} id="light-layout">
                {body}
              </main>
            </LightLayoutContextProvider>
          </ScrollableView>
          <Drawer
            anchor="right"
            variant="persistent"
            open={showDrawer}
            className={clsx(drawerClasses.drawer, {
              [drawerClasses.drawerOpened]: showDrawer
            })}
            classes={{
              paper: drawerClasses.paper,
            }}
          >
            <Toolbar />
            {drawerContent}
          </Drawer>
        </Box>
      </Box>
    </div>
  );
};
